import React from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { renderToStaticMarkup } from 'react-dom/server';
import jsPDF from 'jspdf';
import colors from '../../styles/colors';
import { AppContext } from '../../context/AppContext';
import * as Price from '../../util/Price';
import InvoiceTypes from '../../util/InvoiceTypes';
import Button from '../Button';
import InvoicePdfTemplate from '../invoices/InvoicePdfTemplate';
// import { GET_ORGANIZATION_METRICS } from "../../graphql/queries";

const Container = styled.div`
  padding: 20px 15px;
  overflow-y: auto;
  @media screen and (max-width: 991px) {
    width: 50vw;
  }

  @media screen and (max-width: 450px) {
    width: 80vw;
    max-height: 70vh;
  }
`;

const DarkText = styled.div`
  font-size: 1.4rem;
  color: ${colors.grey1};
  font-weight: ${(props) => props.fontWeight || '600'};
  margin-right: 20px;
`;

const LightText = styled.div`
  font-size: 1.4rem;
  color: ${colors.grey2};
  font-weight: 500;
  text-align: ${(props) => props.align || 'left'};
`;

const Line = styled.div`
  display: flex;
  margin-bottom: 20px;

  &:last-of-type {
    margin-bottom: 0px;
  }
`;

const Break = styled.div`
  height: 1px;
  width: 100%;
  background: ${colors.grey6};
  margin-bottom: 20px;
`;

export default function InvoiceDetailsModal({ org }) {
  const { app } = React.useContext(AppContext);

  if (!app.currentInvoice) {
    return null;
  }

  const invoice = app.currentInvoice;
  const grandTotal = invoice.totalAmountDue + invoice.additionalAmount || 0;

  function generateTemplate() {
    const string = renderToStaticMarkup(
      <InvoicePdfTemplate invoice={invoice} org={org} />,
    );
    const doc = new jsPDF({ orientation: 'p', unit: 'pt', format: 'a4' });
    doc.addFont('Poppins-SemiBold-normal.ttf', 'Poppins', 'normal');
    doc.html(string, {
      callback(doc) {
        doc.save('CTO-Invoice.pdf');
      },
      x: 32,
      y: 32,
      html2canvas: {
        scale: 0.8,
      },
    });
  }

  return (
    <Container>
      {invoice._id !== 'na' && (
        <Line>
          <DarkText>Invoice Id:</DarkText>
          <LightText>{invoice._id}</LightText>
        </Line>
      )}
      {invoice.type === InvoiceTypes.MonthlyPayment ? (
        <Line>
          <DarkText>Billing Period:</DarkText>
          <LightText>
            {`${moment(
              (invoice.subscriptionCycleStartDate || invoice.paidAt) * 1000,
            ).format('MM/D/YYYY')} - ${moment(
              invoice.subscriptionCycleEndDate * 1000,
            ).format('MM/D/YYYY')}`}
          </LightText>
        </Line>
      ) : (
        <Line>
          <DarkText>Date:</DarkText>
          <LightText>
            {moment(invoice.paidAt * 1000).format('MM/D/YYYY')}
          </LightText>
        </Line>
      )}

      {/* <Line>
        <DarkText>Amount:</DarkText>
        <DarkText>{`$${Price.output(invoice.newCustomerCost + invoice.repeatCustomerCost, true)}`}</DarkText>
      </Line>
      */}
      <Line>
        <DarkText>Status:</DarkText>
        <LightText>{invoice.paidAt ? 'Paid' : 'Not yet paid'}</LightText>
      </Line>
      <Line>
        <DarkText>Description:</DarkText>
        <LightText>{invoice.description}</LightText>
      </Line>
      {invoice.type === InvoiceTypes.MonthlyPayment && (
        <>
          <Line>
            <DarkText>Referral Marketing:</DarkText>
            <DarkText>
              $
              {Price.output(
                invoice.newCustomerCost + invoice.repeatCustomerCost,
                true,
              )}
            </DarkText>
          </Line>
          <Line>
            <DarkText fontWeight="400">
              - New Customer contacts acquired:
            </DarkText>
            <LightText>
              {`${invoice.newCustomerAmount}`} (discount activations)
            </LightText>
          </Line>
          <Line>
            <DarkText fontWeight="400">- Repeat Customers:</DarkText>
            <LightText>
              {`${invoice.repeatCustomerAmount}`} (discount activations)
            </LightText>
          </Line>
          <Break />
          <Line>
            <DarkText>Campaign Marketing :</DarkText>
            <DarkText>${Price.output(invoice.campaignCharges, true)}</DarkText>
          </Line>
          {invoice.campaignStats.sms.creditUsed > 0 && (
            <Line>
              <DarkText fontWeight="400">
                - {invoice.campaignStats.sms.creditUsed} credits for{' '}
                {invoice.campaignStats.sms.numberSent} SMS
              </DarkText>
            </Line>
          )}

          {invoice.campaignStats.mms.creditUsed > 0 && (
            <Line>
              <DarkText fontWeight="400">
                - {invoice.campaignStats.mms.creditUsed} credits for{' '}
                {invoice.campaignStats.mms.numberSent} MMS
              </DarkText>
            </Line>
          )}

          <Line>
            <DarkText>Total Service Charges :</DarkText>
            <DarkText>{`$${Price.output(invoice.totalAmountDue, true)}`}</DarkText>
          </Line>

          {invoice.organizationMinMonthlyCharges > invoice.totalAmountDue
            ? (
              <Line>
                <DarkText>Minimum Monthly Charge :</DarkText>
                <DarkText>{`$${Price.output(invoice.organizationMinMonthlyCharges, true)}`}</DarkText>
              </Line>
            )
            : null}

        </>

      )}
      <Break />
      <Line>
        <DarkText>Total Billed Amount : </DarkText>
        <DarkText>{`$${Price.output(grandTotal, true)}`}</DarkText>
      </Line>
      <Break />
      <LightText align="center">Thank you for your business!</LightText>
      <div
        style={{
          display: 'flex',
          width: '100%',
          justifyContent: 'center',
          marginTop: '20px',
        }}
      >
        <Button
          text="Download Invoice"
          type="button"
          onClick={generateTemplate} //eslint-disable-line
          width="max-content"
        />
      </div>

      {/* {invoice.type === InvoiceTypes.MonthlyPayment && (
        <>
          <Break />
          <Line>
            <DarkText>
              New customer referrals
            </DarkText>
            <LightText>
              {`${invoice.newCustomerAmount} ($${Price.output(invoice.newCustomerCost, true)})`}
            </LightText>
          </Line>
          <Line>
            <DarkText>
              Returning customer referrals
            </DarkText>
            <LightText>
              {`${invoice.repeatCustomerAmount} ($${Price.output(invoice.repeatCustomerCost, true)})`}
            </LightText>
          </Line>
          <Line>
            <DarkText>
              Affiliate customer referrals
            </DarkText>
            <LightText>
              {`${invoice.affiliateCustomerAmount} ($${Price.output(invoice.affiliateCustomerCost, true)})`}
            </LightText>
          </Line>
        </>
      )} */}
    </Container>
  );
}
